/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  AutocompleteFormContainer
 */

import { useUnmount } from '@zcode-ui/hook'
import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import type { AutocompleteApiResult, AutocompleteCurrentPositionResult } from '@zcode/bizos'
import type { Dispatch } from '@zcode/state-type'
import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import type * as Api from '../../../../shared/api'
import {
  type AutocompleteOrigin,
  MIN_CHARACTER_COUNT,
} from '../../../../shared/components/Autocomplete/constants'
import {
  addResultToPreviousSearches,
  getInputValue,
  getManualSearchResult,
  stripSearchTerm,
} from '../../../../shared/components/Autocomplete/helpers'
import fetchPredictionResults from '../../../../shared/components/Autocomplete/module/thunks/fetchPredictionResults'
import manualSearch from '../../../../shared/components/Autocomplete/module/thunks/manualSearch'
import { selectIsCurrentLocationLoading } from '../../../../shared/modules/autocomplete/selectors'
import { throttle } from '../../../../shared/utils/utils'
import { useAutocompleteContext } from '../AutocompleteContext'
import { getPredictionText } from '../helpers/getPredictionText'
import { getPreviousSearches } from '../helpers/getPreviousSearches'
import { type PageNameValues, trackSearchBarClick } from '../trackSearchBarClick'
import { AutocompleteForm, type AutocompleteFormProps } from './AutocompleteForm'

interface InnerProps
  extends Omit<
    AutocompleteFormProps,
    | 'hoveredIndex'
    | 'onInputChange'
    | 'onManualSearch'
    | 'isMobile'
    | 'isCurrentLocationLoading'
    | 'onFillInput'
    | 'predictionResult'
  > {
  pageName?: PageNameValues
  exclude: Parameters<(typeof Api)['autocompleteResults']['query']>[0]['exclude']
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  isShortTerm?: boolean
  onError?: (error: { message: string }) => void
  onSelectAutocompleteResult: (
    result: AutocompleteApiResult | AutocompleteCurrentPositionResult
  ) => void
  origin?: AutocompleteOrigin
  resetForm: () => void
  setFieldValue: (field: 'searchTerm', value: string, validate?: boolean | undefined) => void
}

export const AutocompleteFormContainer = ({
  pageName,
  exclude,
  handleChange,
  isShortTerm,
  onError,
  onSelectAutocompleteResult,
  origin,
  resetForm,
  setFieldValue,
  theme,
  ...forwardProps
}: InnerProps) => {
  const dispatch = useDispatch<Dispatch>()
  const {
    hoveredIndex,
    lat,
    lng,
    predictionResults,
    prevSearchTerm,
    searchTerm,
    setIsFocused,
    setForceContextualMenuOpen,
    setKeystrokes,
    setPredictionResults,
    setPrevSearchTerm,
    setSearchTerm,
  } = useAutocompleteContext()

  // routeProps
  const { pathname } = useLocation()
  const pathnameParts = pathname?.split('/')
  const isAddressPage = !!pathnameParts && pathnameParts?.[1] === 'address'

  // state
  const isCurrentLocationLoading = useSelector(selectIsCurrentLocationLoading)
  const isMobile = useSelector(selectIsMobile)

  // dispatch
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target) {
      const value = e.target.value
      const trimmedValue = stripSearchTerm(value)

      if (trimmedValue !== prevSearchTerm) {
        setSearchTerm(trimmedValue)
        setPrevSearchTerm(trimmedValue)
        if (trimmedValue.length < MIN_CHARACTER_COUNT) {
          setPredictionResults(null)
        } else {
          // we are not sending lat/lng for permanent address page (ZUM-54986)
          dispatch(
            fetchPredictionResults(
              isAddressPage,
              exclude,
              trimmedValue,
              lat,
              lng,
              setPredictionResults
            )
          )
        }
      }
    }
  }

  const onFillInput = (e: React.FormEvent) => {
    e.preventDefault()

    const prevSearches = getPreviousSearches(exclude)
    const result = getManualSearchResult({
      hoveredIndex,
      predictionResults,
      prevSearches,
      isShortTerm,
      searchTerm,
    })
    if (result) {
      const value = getInputValue(result)
      addResultToPreviousSearches(result)
      onSelectAutocompleteResult(result)
      setFieldValue('searchTerm', value)
      setForceContextualMenuOpen(false)
      setIsFocused(false)
    }
  }

  const throttledHandleChange = useCallback(
    throttle(handleInputChange, 400, {
      leading: false,
      trailing: true,
    }),
    [handleInputChange]
  )
  useUnmount(() => {
    throttledHandleChange.cancel()
  })

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.persist()
      handleChange(e)
      setKeystrokes((keystrokes) => keystrokes + 1)
      throttledHandleChange(e)
    },
    [dispatch, throttledHandleChange]
  )
  const onManualSearch = (e: React.FormEvent) => {
    // passing down redux's dispatch
    e.preventDefault()
    resetForm()
    dispatch(
      manualSearch({
        exclude,
        onSelectAutocompleteResult,
        onError,
        origin,
        hoveredIndex,
        predictionResults,
        searchTerm,
        setPredictionResults,
        setSearchTerm,
      })
    )
    setIsFocused(false)
  }

  const handleOnSearchBarClick = () => {
    if (!pageName) return
    dispatch(trackSearchBarClick({ pageName }))
  }

  const finalProps = {
    isAddressPage,
    isCurrentLocationLoading,
    isMobile,
    handleInputChange,
    handleOnSearchBarClick,
    onFillInput,
    onInputChange,
    onManualSearch,
    value: true,
    hoveredIndex,
    predictionResult: predictionResults?.length
      ? getPredictionText(predictionResults[0], searchTerm)
      : null,
    exclude,
    resetForm,
    setFieldValue,
    theme,
    ...forwardProps,
  }
  return <AutocompleteForm {...finalProps} />
}
